/**
 * Support chat hook — PRIVATE 1-to-1 user↔admin chat model.
 *
 * Per docs/FINAL-VERSION-CHAT.md §5.0:
 * - Store both conversationId and roomId from every conversation response (list, with-admin, start-new).
 * - Socket join: prefer roomId with join-room when present; else conversationId with conversation:join.
 * - Socket leave: send both in conversation:leave when available.
 * - Socket send: roomId or conversationId (backend accepts both).
 * - Incoming messages: match by payload.roomId === activeRoomId OR payload.conversationId === activeConversationId.
 * - REST: conversationId for :id routes and POST body; roomId for GET /rooms/:roomId/messages when only roomId available.
 */
import {useState, useCallback, useEffect, useRef, useMemo} from 'react'
import * as chatSupportService from '../services/chatSupportService'
import type {ChatConversation, ChatMessage, MessageType} from '../services/chatSupportTypes'
import {getConversationId, getRoomId} from '../services/chatSupportTypes'
import socketService from '../services/socketService'
import {chatConversationMessagesUrl} from '../redux/api'
import axios, {AxiosError} from 'axios'
import useAuth from './useAuth'

const MESSAGES_POLL_MS = 8000

interface JoinAckSuccess {
  success: true
  conversationId?: string
  roomId?: string
  autoFixed?: boolean
  message?: string
}

interface JoinRoomAckSuccess {
  success: true
  roomId?: string
  conversationId?: string
}

interface AckError {
  error: string
  status: number
}

function normalizeMessage(
  msg: ChatMessage,
  conversationId: string,
  currentUserId: string | undefined
): ChatMessage {
  const raw = msg as ChatMessage & { conversationId?: string }
  const convId = raw.conversationId ?? conversationId
  const sender =
    typeof raw.senderId === 'object' && raw.senderId !== null
      ? raw.senderId
      : { _id: String(raw.senderId), firstName: '', lastName: '' }
  const senderIdStr = sender._id ?? ''
  const isMe = Boolean(currentUserId && senderIdStr === currentUserId)
  
  // Determine message type
  let messageType: MessageType = 'user'
  if (raw.isAI) {
    messageType = 'ai'
  } else if (isMe) {
    messageType = 'user'
  } else {
    // Check if sender is admin (role 2) - this is a heuristic, backend should provide better info
    messageType = 'admin'
  }
  
  // For AI messages, set sender name
  const senderName = raw.isAI 
    ? 'Maarefati Assistant' 
    : isMe 
      ? 'You' 
      : [sender.firstName, sender.lastName].filter(Boolean).join(' ') || 'User'
  
  return {
    ...raw,
    conversationId: convId,
    senderId: sender as ChatMessage['senderId'],
    isMe,
    senderName,
    avatar: raw.isAI ? undefined : (sender.avatar ?? raw.avatar), // AI messages don't have avatar
    messageType,
  }
}

function sortMessages(msgs: ChatMessage[]): ChatMessage[] {
  return [...msgs].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
}

export function useSupportChat(isAdmin: boolean) {
  const {me} = useAuth()
  const currentUserId = me?._id
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [selectedConversation, setSelectedConversationState] = useState<ChatConversation | null>(null)
  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, ChatMessage[]>>({})
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chatAvailable, setChatAvailable] = useState<boolean | null>(null)
  const [aiConversation, setAiConversation] = useState<ChatConversation | null>(null)
  const [aiTyping, setAiTyping] = useState<Record<string, boolean>>({}) // conversationId -> isTyping
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const loadedConversationIdsRef = useRef<Set<string>>(new Set())
  /** AI conversation id from GET /api/chat/ai/conversation; used so send always uses POST /api/chat/ai/message when in AI chat. */
  const aiConversationIdRef = useRef<string | null>(null)
  /** Mirror of ref so UI (isAiConversation) updates when we set the id in useEffect (e.g. after selecting conv from list). */
  const [aiConversationIdState, setAiConversationIdState] = useState<string | null>(null)
  const activeConvIdRef = useRef<string | null>(null)
  const activeRoomIdRef = useRef<string | null>(null)
  activeConvIdRef.current = getConversationId(selectedConversation)
  activeRoomIdRef.current = getRoomId(selectedConversation)

  const isUnavailable = (e: unknown) =>
    e instanceof chatSupportService.ChatApiUnavailableError ||
    chatSupportService.isChatApiUnavailable(e)

  // Compute message count for current conversation to use as dependency
  const currentConvIdMemo = getConversationId(selectedConversation)
  const currentMessageCount = currentConvIdMemo ? (messagesByConversation[currentConvIdMemo]?.length ?? 0) : 0
  
  const visibleMessages = useMemo(() => {
    const convId = getConversationId(selectedConversation)
    if (!convId) return []
    const msgs = messagesByConversation[convId]
    // Always return a new array reference to ensure React detects changes
    // Use currentMessageCount as dependency to force recomputation when messages are added
    return msgs ? [...msgs] : []
  }, [selectedConversation, messagesByConversation, currentMessageCount])

  /**
   * Global Socket.IO message listener: listens to ALL chat messages and updates state.
   * This ensures messages are received even when switching conversations or when socket reconnects.
   */
  useEffect(() => {
    const socket = socketService.getSocket()
    if (!socket) return

    const globalHandler = (payload: { conversationId?: string; roomId?: string; message?: ChatMessage }) => {
      const { conversationId: payloadConvId, roomId: payloadRoomId, message } = payload
      if (!message) return

      const messageConvId = (message as ChatMessage & { conversationId?: string }).conversationId ?? payloadConvId
      if (!messageConvId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[chat] global handler: message missing conversationId', message._id)
        }
        return
      }

      // Add message to state for its conversation (even if not currently selected)
      setMessagesByConversation((prev) => {
        const list = prev[messageConvId] ?? []
        if (list.some((m) => m._id === message._id)) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[chat] 🌐 global handler: message already exists', message._id)
          }
          return prev
        }
        
        const withIds = { ...message, conversationId: messageConvId } as ChatMessage
        const normalized = normalizeMessage(withIds, messageConvId, currentUserId)
        const next = sortMessages([...list, normalized])
        
        
        // Always create a new object to ensure React detects the change
        return { ...prev, [messageConvId]: next }
      })

      // Update conversation's lastMessage and unreadCount
      setConversations((prev) => {
        return prev.map((conv) => {
          const convId = getConversationId(conv)
          if (convId !== messageConvId) return conv
          
          // Check if this is the currently selected conversation (don't increment unread if viewing it)
          const isCurrentConversation = convId === activeConvIdRef.current
          
          return {
            ...conv,
            lastMessage: {
              _id: message._id,
              content: message.content,
              createdAt: message.createdAt,
              senderId: typeof message.senderId === 'object' ? message.senderId._id : message.senderId,
              read: message.read,
            },
            updatedAt: message.createdAt,
            // Only increment unread if not viewing this conversation
            unreadCount: isCurrentConversation ? 0 : (conv.unreadCount ?? 0) + 1,
          }
        })
      })
      
      // Auto-scroll if this message is for the currently selected conversation
      if (messageConvId === activeConvIdRef.current) {
        // Trigger scroll by updating a ref or state that the UI watches
        // The UI component already watches messages.length, so this should work automatically
      }
    }

    socket.on('chat:message:new', globalHandler)

    return () => {
      socket.off('chat:message:new', globalHandler)
    }
  }, [currentUserId])

  // AI Assistant event listeners
  useEffect(() => {
    const socket = socketService.getSocket()
    if (!socket) return

    // Handle AI typing indicator
    const handleAiTyping = (data: { conversationId: string; isTyping: boolean }) => {
      if (data.conversationId) {
        setAiTyping((prev) => ({
          ...prev,
          [data.conversationId]: data.isTyping,
        }))
      }
    }

    // Handle AI reply (message already saved by backend; may be emitted as { message } or { aiMessage })
    const handleAiReply = (data: {
      conversationId: string
      roomId?: string
      message?: ChatMessage
      aiMessage?: ChatMessage
      confidence?: number
      source?: 'faq' | 'ai'
    }) => {
      const convId = data.conversationId
      if (!convId) return
      const msg = data.message ?? data.aiMessage
      if (!msg || !(msg._id || msg.content != null)) return

      setMessagesByConversation((prev) => {
        const list = prev[convId] ?? []
        if (list.some((m) => m._id === msg._id)) {
          return prev
        }
        const withIds = { ...msg, conversationId: convId, isAI: true, confidence: data.confidence, source: data.source } as ChatMessage
        const normalized = normalizeMessage(withIds, convId, currentUserId)
        const next = sortMessages([...list, normalized])
        return { ...prev, [convId]: next }
      })

      // Clear typing indicator
      setAiTyping((prev) => ({
        ...prev,
        [convId]: false,
      }))

    }

    // Handle escalation
    const handleEscalation = (data: { conversationId: string; roomId?: string; reason?: string }) => {
      const convId = data.conversationId
      if (!convId) return

      // Create system message for escalation
      const escalationMessage: ChatMessage = {
        _id: `escalation_${Date.now()}_${Math.random()}`,
        conversationId: convId,
        roomId: data.roomId,
        senderId: { _id: 'system', firstName: 'System', lastName: '' },
        content: "I'm forwarding this to a human assistant 👨‍💼",
        createdAt: new Date().toISOString(),
        read: false,
        isAI: false,
        messageType: 'system',
      }

      // Add escalation message to state
      setMessagesByConversation((prev) => {
        const list = prev[convId] ?? []
        const normalized = normalizeMessage(escalationMessage, convId, currentUserId)
        const next = sortMessages([...list, normalized])
        return { ...prev, [convId]: next }
      })

      // Clear typing indicator
      setAiTyping((prev) => ({
        ...prev,
        [convId]: false,
      }))

    }

    socket.on('ai:typing', handleAiTyping)
    socket.on('ai:reply', handleAiReply)
    socket.on('ai:escalate', handleEscalation)

    return () => {
      socket.off('ai:typing', handleAiTyping)
      socket.off('ai:reply', handleAiReply)
      socket.off('ai:escalate', handleEscalation)
    }
  }, [currentUserId])

  /** When non-admin selects a conversation, ensure we know the AI conversation id (so send uses POST /api/chat/ai/message when in AI chat). */
  useEffect(() => {
    if (isAdmin || !selectedConversation) return
    if (aiConversationIdRef.current != null) return
    let cancelled = false
    chatSupportService
      .getOrCreateAiConversation()
      .then((conv) => {
        if (cancelled) return
        const id = getConversationId(conv)
        if (id) {
          aiConversationIdRef.current = id
          setAiConversationIdState(id)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isAdmin, selectedConversation])

  /** Whether the currently selected conversation is the AI conversation (for UI: title, "Talk to admin" button). Prefer API flag from GET /api/chat/conversations; fallback to stored id. */
  const isAiConversation = useMemo(() => {
    if (selectedConversation?.isAiConversation === true) return true
    const selId = getConversationId(selectedConversation)
    const aiId = aiConversationIdRef.current ?? aiConversationIdState
    return Boolean(selId && aiId && selId === aiId)
  }, [selectedConversation, aiConversationIdState])

  /** Reset all chat state (mandatory helper). */
  const resetChatState = useCallback(() => {
    setSelectedConversationState(null)
    setAiConversation(null)
    aiConversationIdRef.current = null
    setAiConversationIdState(null)
    setMessagesByConversation({})
    loadedConversationIdsRef.current.clear()
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  /** Load message history for a conversation (REST, conversationId ONLY). */
  const loadMessages = useCallback(
    async (conversationId: string) => {
      setError(null)
      loadedConversationIdsRef.current.add(conversationId)
      try {
        const msgs = await chatSupportService.fetchMessages(conversationId)
        const withConvId = msgs.map((m) => ({
          ...m,
          conversationId: (m as ChatMessage & { conversationId?: string }).conversationId ?? conversationId,
        }))
        const normalized = withConvId.map((m) => normalizeMessage(m, conversationId, currentUserId))
        const sorted = sortMessages(normalized)
        setMessagesByConversation((prev) => ({ ...prev, [conversationId]: sorted }))
        await chatSupportService.markConversationRead(conversationId)
        if (process.env.NODE_ENV === 'development') {
          console.debug('[chat] loadMessages', conversationId, sorted.length, 'messages')
        }
      } catch (e: unknown) {
        loadedConversationIdsRef.current.delete(conversationId)
        const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string }
        const status = err?.response?.status
        if (status === 404) {
          resetChatState()
          setError('Conversation not found')
        } else if (status === 403 && !isAdmin) {
          // User not a participant - try to get/create correct conversation
          if (process.env.NODE_ENV === 'development') {
            console.warn('[chat] 403 on messages - user not participant, attempting auto-fix')
          }
          resetChatState()
          try {
            const correctConv = await chatSupportService.getOrCreateConversationWithAdmin()
            setConversations([correctConv])
            setSelectedConversationState(correctConv)
            const correctConvId = getConversationId(correctConv)
            if (correctConvId) {
              const socket = socketService.getSocket()
              if (socket?.connected) {
                socket.emit('conversation:join', { conversationId: correctConvId }, (res: JoinAckSuccess | AckError) => {
                  if ((res as JoinAckSuccess).success) {
                    loadMessages(correctConvId)
                  }
                })
              } else {
                loadMessages(correctConvId)
              }
            }
          } catch (fixError) {
            setError('Unable to access conversation. Please try again.')
          }
        } else {
          setError(err?.response?.data?.message || err?.message || 'Failed to load messages')
        }
      }
    },
    [currentUserId, resetChatState, isAdmin]
  )

  /** Non-admin: get/create conversation (REST), then join: prefer join-room with roomId when present. */
  const loadConversationWithAdmin = useCallback(async () => {
    if (isAdmin) return
    setLoading(true)
    setError(null)
    try {
      const conv = await chatSupportService.getOrCreateConversationWithAdmin()
      setChatAvailable(true)
      setConversations([conv])
      setSelectedConversationState(conv)
      const conversationId = getConversationId(conv)
      if (!conversationId) throw new Error('Conversation missing ID')
      const roomId = getRoomId(conv)
      const socket = socketService.getSocket()

      if (socket?.connected) {
        const onJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
          if ((res as AckError).error) {
            const errRes = res as AckError
            if (process.env.NODE_ENV === 'development') {
              console.warn('[chat] join failed', errRes.status, errRes.error)
            }
            setError(errRes.error || 'Could not join chat room')
          } else {
            const successRes = res as JoinAckSuccess
            if (successRes.autoFixed && successRes.conversationId) {
              const fixedConvId = successRes.conversationId
              chatSupportService.fetchConversations().then((list) => {
                const fixedConv = list.find((c) => getConversationId(c) === fixedConvId)
                if (fixedConv) {
                  setConversations([fixedConv])
                  setSelectedConversationState(fixedConv)
                  if (successRes.message) {
                    setError(null)
                    if (process.env.NODE_ENV === 'development') console.info('[chat]', successRes.message)
                  }
                  loadMessages(fixedConvId)
                }
              })
            } else {
              loadMessages(conversationId)
            }
          }
        }
        if (roomId) {
          socket.emit('join-room', { roomId }, onJoin)
        } else {
          socket.emit('conversation:join', { conversationId }, onJoin)
        }
      } else {
        loadMessages(conversationId)
      }
    } catch (e: unknown) {
      if (isUnavailable(e)) {
        setChatAvailable(false)
        const status = (e as { status?: number })?.status
        setError(
          status === 500
            ? 'Chat service is temporarily unavailable. Please try again later.'
            : 'Chat is not available yet.'
        )
      } else {
        setError((e as Error)?.message || 'Failed to load chat')
      }
      resetChatState()
    } finally {
      setLoading(false)
    }
  }, [isAdmin, loadMessages, resetChatState])

  /**
   * Non-admin: get or create AI conversation (GET /api/chat/ai/conversation), then join and load messages.
   * 403 for admin; 503 if AI chat is not configured.
   */
  const loadAiConversation = useCallback(async () => {
    if (isAdmin) return
    setLoading(true)
    setError(null)
    try {
      const conv = await chatSupportService.getOrCreateAiConversation()
      setChatAvailable(true)
      const conversationId = getConversationId(conv)
      if (!conversationId) throw new Error('AI conversation missing ID')
      const aiConvWithFlag = { ...conv, isAiConversation: true }
      aiConversationIdRef.current = conversationId
      setAiConversationIdState(conversationId)
      setAiConversation(aiConvWithFlag)
      setConversations([aiConvWithFlag])
      setSelectedConversationState(aiConvWithFlag)
      const roomId = getRoomId(conv)
      const socket = socketService.getSocket()

      if (socket?.connected) {
        const onJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
          if ((res as AckError).error) {
            setError((res as AckError).error || 'Could not join chat room')
          } else {
            loadMessages(conversationId)
          }
        }
        if (roomId) {
          socket.emit('join-room', { roomId }, onJoin)
        } else {
          socket.emit('conversation:join', { conversationId }, onJoin)
        }
      } else {
        loadMessages(conversationId)
      }
    } catch (e: unknown) {
      if (e instanceof chatSupportService.ChatApiUnavailableError) {
        setChatAvailable(false)
        const status = e.status
        setError(
          status === 503
            ? (e.serverMessage || 'AI chat is not configured.')
            : status === 403
              ? 'Only non-admin users can use AI chat.'
              : 'Chat is not available yet.'
        )
      } else if (isUnavailable(e)) {
        setChatAvailable(false)
        setError('Chat is not available yet.')
      } else {
        setError((e as Error)?.message || 'Failed to load AI chat')
      }
      resetChatState()
    } finally {
      setLoading(false)
    }
  }, [isAdmin, loadMessages, resetChatState])

  /**
   * Non-admin: switch from AI chat to admin conversation (POST with-admin), then select and join.
   */
  const switchToAdminConversation = useCallback(async () => {
    if (isAdmin) return
    setLoading(true)
    setError(null)
    try {
      const conv = await chatSupportService.getOrCreateConversationWithAdmin()
      setChatAvailable(true)
      setConversations((prev) => {
        const convId = getConversationId(conv)
        if (!convId) return prev
        const exists = prev.some((c) => getConversationId(c) === convId)
        if (exists) return prev
        return [...prev, conv]
      })
      setSelectedConversationState(conv)
      const conversationId = getConversationId(conv)
      if (!conversationId) throw new Error('Conversation missing ID')
      const roomId = getRoomId(conv)
      const socket = socketService.getSocket()

      if (socket?.connected) {
        const onJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
          if (!(res as AckError).error) {
            loadMessages(conversationId)
          }
        }
        if (roomId) socket.emit('join-room', { roomId }, onJoin)
        else socket.emit('conversation:join', { conversationId }, onJoin)
      } else {
        loadMessages(conversationId)
      }
    } catch (e: unknown) {
      if (isUnavailable(e)) {
        setChatAvailable(false)
        setError('Chat is not available yet.')
      } else {
        setError((e as Error)?.message || 'Failed to switch to admin')
      }
    } finally {
      setLoading(false)
    }
  }, [isAdmin, loadMessages])

  const loadConversations = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await chatSupportService.fetchConversations()
      const filteredList = isAdmin
        ? list
        : list.filter((conv) => {
            const participantIds = conv.participants.map((p) => p._id)
            return currentUserId && participantIds.includes(currentUserId) && conv.participants.length === 2
          })
      setChatAvailable(true)
      setConversations(filteredList)
    } catch (e: unknown) {
      if (isUnavailable(e)) {
        setChatAvailable(false)
        setConversations([])
        setError(null)
      } else {
        setError((e as Error)?.message || 'Failed to load conversations')
        setConversations([])
      }
    } finally {
      setLoading(false)
    }
  }, [isAdmin, currentUserId])

  const setSelectedConversation = useCallback(
    (conv: ChatConversation | null) => {
      setSelectedConversationState(conv)
      const convId = getConversationId(conv)
      if (conv && convId && !loadedConversationIdsRef.current.has(convId)) {
        loadMessages(convId)
      }
    },
    [loadMessages]
  )

  useEffect(() => {
    if (!selectedConversation) return
    const id = getConversationId(selectedConversation)
    if (!id || loadedConversationIdsRef.current.has(id)) return
    loadMessages(id)
  }, [selectedConversation, loadMessages])

  /** Poll selected conversation (fallback when Socket not connected). */
  useEffect(() => {
    if (!selectedConversation) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    const socket = socketService.getSocket()
    if (socket?.connected) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    const conversationId = getConversationId(selectedConversation)
    if (!conversationId) return
    let consecutive500s = 0
    const MAX_CONSECUTIVE_500S = 3
    const poll = async () => {
      const currentSocket = socketService.getSocket()
      if (currentSocket?.connected) {
        if (pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
        return
      }
      try {
        const res = await axios.get<{ messages: ChatMessage[] }>(chatConversationMessagesUrl(conversationId))
        consecutive500s = 0
        const msgs = res.data?.messages ?? []
        const withConvId = msgs.map((m) => ({
          ...m,
          conversationId: (m as ChatMessage & { conversationId?: string }).conversationId ?? conversationId,
        }))
        const normalized = withConvId.map((m) => normalizeMessage(m, conversationId, currentUserId))
        const sorted = sortMessages(normalized)
        setMessagesByConversation((prev) => ({ ...prev, [conversationId]: sorted }))
      } catch (e) {
        const status = (e as AxiosError)?.response?.status
        if (status === 404) {
          resetChatState()
          if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
        } else if (status === 500) {
          consecutive500s++
          if (consecutive500s >= MAX_CONSECUTIVE_500S) {
            if (pollRef.current) {
              clearInterval(pollRef.current)
              pollRef.current = null
            }
          }
        }
      }
    }
    pollRef.current = setInterval(poll, MESSAGES_POLL_MS)
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [selectedConversation, currentUserId, resetChatState])

  /**
   * Socket.IO: prefer join-room with roomId when present; else conversation:join with conversationId.
   * Leave: send both conversationId and roomId when available so both rooms are left.
   * Incoming: accept message if payload.roomId === activeRoomId OR payload.conversationId === activeConversationId.
   */
  useEffect(() => {
    const conv = selectedConversation
    const convId = getConversationId(conv)
    const roomId = getRoomId(conv)
    if (!convId) return
    const socket = socketService.getSocket()
    if (!socket) return

    let isCleanedUp = false
    let handler: ((payload: { conversationId?: string; roomId?: string; message?: ChatMessage }) => void) | null = null

    const onJoinAck = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError, fallbackConvId: string) => {
      if (isCleanedUp) return
      if ((res as AckError).error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[chat] join error', (res as AckError).status, (res as AckError).error)
        }
        if (!loadedConversationIdsRef.current.has(fallbackConvId)) loadMessages(fallbackConvId)
        return
      }
      const successRes = res as JoinAckSuccess | JoinRoomAckSuccess
      if (successRes.success) {
        const withAutoFix = res as JoinAckSuccess
        if (withAutoFix.autoFixed && withAutoFix.conversationId) {
          const fixedConvId = withAutoFix.conversationId
          chatSupportService.fetchConversations().then((list) => {
            const fixedConv = list.find((c) => getConversationId(c) === fixedConvId)
            if (fixedConv) {
              setConversations((prev) => {
                const exists = prev.some((c) => getConversationId(c) === fixedConvId)
                if (exists) return prev
                return [...prev, fixedConv]
              })
              setSelectedConversationState(fixedConv)
              if (withAutoFix.message && process.env.NODE_ENV === 'development') {
                console.info('[chat]', withAutoFix.message)
              }
              if (!loadedConversationIdsRef.current.has(fixedConvId)) loadMessages(fixedConvId)
            }
          })
        } else if (!loadedConversationIdsRef.current.has(fallbackConvId)) {
          loadMessages(fallbackConvId)
        }
      }
    }

    const joinRoom = () => {
      if (!socket.connected || isCleanedUp) return
      if (roomId) {
        socket.emit('join-room', { roomId }, (res: JoinRoomAckSuccess | AckError) => onJoinAck(res, convId))
      } else {
        socket.emit('conversation:join', { conversationId: convId }, (res: JoinAckSuccess | AckError) => onJoinAck(res, convId))
      }
    }

    handler = (payload: { conversationId?: string; roomId?: string; message?: ChatMessage }) => {
      const { conversationId: payloadConvId, roomId: payloadRoomId, message } = payload
      if (!message) {
        if (process.env.NODE_ENV === 'development') console.warn('[chat] chat:message:new missing message', payload)
        return
      }
      // Get current conversation from refs (always up-to-date)
      const currentConvId = activeConvIdRef.current
      const currentRoomId = activeRoomIdRef.current
      const messageConvId = (message as ChatMessage & { conversationId?: string }).conversationId ?? payloadConvId
      
      // Check if message belongs to current conversation
      const matchesRoom = currentRoomId && payloadRoomId && payloadRoomId === currentRoomId
      const matchesConv = currentConvId && messageConvId && messageConvId === currentConvId
      
      // Also check if message belongs to any conversation we have loaded (for multi-conversation support)
      const belongsToLoadedConv = messageConvId && loadedConversationIdsRef.current.has(messageConvId)
      
      // Accept message if it matches current conversation OR belongs to a loaded conversation
      if (!matchesRoom && !matchesConv && !belongsToLoadedConv) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[chat] ignoring message for other conversation', { 
            payloadRoomId, 
            payloadConvId, 
            messageConvId,
            currentRoomId, 
            currentConvId,
            belongsToLoadedConv,
            loadedConvs: Array.from(loadedConversationIdsRef.current)
          })
        }
        return
      }
      
      // Use message's conversationId or fallback to current/payload
      const keyConvId = messageConvId ?? currentConvId ?? payloadConvId ?? ''
      if (!keyConvId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[chat] cannot determine conversationId for message', message._id)
        }
        return
      }
      
      // Add message to state
      setMessagesByConversation((prev) => {
        const list = prev[keyConvId] ?? []
        // Skip if message already exists
        if (list.some((m) => m._id === message._id)) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[chat] message already exists', keyConvId, message._id)
          }
          return prev
        }
        const withIds = { ...message, conversationId: messageConvId ?? keyConvId } as ChatMessage
        const normalized = normalizeMessage(withIds, keyConvId, currentUserId)
        const next = sortMessages([...list, normalized])
        
        return { ...prev, [keyConvId]: next }
      })
    }

    // Register handler BEFORE checking connection to ensure it's always listening
    socket.on('chat:message:new', handler)
    
    // Join room immediately if connected, or when socket connects/reconnects
    const ensureJoined = () => {
      if (!isCleanedUp && socket.connected) {
        joinRoom()
      }
    }
    
    if (socket.connected) {
      joinRoom()
    }
    
    // Listen for connect/reconnect events
    socket.on('connect', ensureJoined)
    

    return () => {
      isCleanedUp = true
      if (handler) socket.off('chat:message:new', handler)
      socket.off('connect', ensureJoined)
      if (socket.connected) {
        const leavePayload: { conversationId?: string; roomId?: string } = {}
        if (convId) leavePayload.conversationId = convId
        if (roomId) leavePayload.roomId = roomId
        socket.emit('conversation:leave', leavePayload, () => {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[chat] left conversation', convId, roomId ?? '')
          }
        })
      }
    }
  }, [selectedConversation, currentUserId, loadMessages])

  /**
   * Send message: for AI conversation uses POST /api/chat/ai/message; otherwise chat:send (or REST fallback).
   */
  const sendMessage = useCallback(
    async (content: string) => {
      const conv = selectedConversation
      if (!conv || !content.trim()) return
      const conversationIdRaw = getConversationId(conv)
      if (!conversationIdRaw) {
        setError('Conversation ID missing')
        return
      }
      const conversationId: string = conversationIdRaw
      const roomId = getRoomId(conv)
      const trimmedContent = content.trim()
      const socket = socketService.getSocket()

      setSending(true)
      setError(null)

      const addMessageToConversation = (message: ChatMessage) => {
        const withConvId = {...message, conversationId} as ChatMessage
        const normalized = normalizeMessage(withConvId, conversationId, currentUserId)
        setMessagesByConversation((prev) => {
          const list = prev[conversationId] ?? []
          if (list.some((m: ChatMessage) => m._id === normalized._id)) return prev
          const next = sortMessages([...list, normalized])
          return {...prev, [conversationId]: next}
        })
      }

      // AI conversation: use POST /api/chat/ai/message when conversation.isAiConversation === true (from GET /api/chat/conversations); else POST /api/chat/messages.
      const currentConvIdStr = conversationId ? String(conversationId) : null
      const fromApiFlag = selectedConversation?.isAiConversation === true
      let aiId = aiConversationIdRef.current ?? aiConversationIdState
      // Fallback: if we don't know the AI conv id yet (e.g. user selected from list before flag existed), fetch once.
      if (!isAdmin && currentConvIdStr && !fromApiFlag && !aiId) {
        try {
          const aiConv = await chatSupportService.getOrCreateAiConversation()
          const id = getConversationId(aiConv)
          if (id) {
            aiConversationIdRef.current = id
            setAiConversationIdState(id)
            aiId = id
          }
        } catch (_) { /* ignore */ }
      }
      const isAiConv = !isAdmin && currentConvIdStr && (fromApiFlag || currentConvIdStr === aiId)
      if (isAiConv) {
        try {
          const response = await chatSupportService.sendAiMessage(conversationId, trimmedContent)
          const userMessage = response?.userMessage
          const aiMessage = response?.aiMessage ?? response?.message
          if (userMessage && (userMessage._id || userMessage.content != null)) {
            addMessageToConversation({ ...userMessage, conversationId } as ChatMessage)
          }
          if (aiMessage && (aiMessage._id || aiMessage.content != null)) {
            addMessageToConversation({ ...aiMessage, conversationId, isAI: true } as ChatMessage)
          }
          // If no aiMessage in response, backend may send it via Socket (ai:reply) — no error
        } catch (e: unknown) {
          const err = e as { status?: number; response?: { data?: { message?: string; en?: string; fr?: string } } }
          if (err?.status === 503) {
            const data = err?.response?.data
            setError(data?.en ?? data?.fr ?? data?.message ?? 'AI chat is not configured.')
          } else {
            setError((e as Error)?.message || 'Failed to send message to AI')
          }
        } finally {
          setSending(false)
        }
        return
      }

      const sendPayload = roomId ? { roomId, content: trimmedContent } : { conversationId, content: trimmedContent }

      if (socket?.connected) {
        return new Promise<void>((resolve) => {
          socket.emit(
            'chat:send',
            sendPayload,
            (res: { message?: ChatMessage } & AckError) => {
              if (res?.error) {
                chatSupportService
                  .sendMessage(conversationId, trimmedContent)
                  .then((msg) => {
                    addMessageToConversation(msg)
                    setSending(false)
                    resolve()
                  })
                  .catch((e: unknown) => {
                    const err = e as { status?: number }
                    if (err?.status === 403 && !isAdmin) {
                      // User not a participant - try to get/create correct conversation
                      resetChatState()
                      chatSupportService.getOrCreateConversationWithAdmin().then((correctConv) => {
                        setConversations([correctConv])
                        setSelectedConversationState(correctConv)
                        setError('Switched to your conversation with admin. Please try sending again.')
                      }).catch(() => {
                        setError("You don't have permission to send messages.")
                      })
                    } else if (isUnavailable(e) && err?.status === 403) {
                      setError("You don't have permission to send messages.")
                    } else {
                      setError((e as Error)?.message || 'Failed to send')
                    }
                    setSending(false)
                    resolve()
                  })
              } else if (res?.message) {
                addMessageToConversation(res.message)
                setSending(false)
                resolve()
              } else {
                chatSupportService
                  .sendMessage(conversationId, trimmedContent)
                  .then((msg) => {
                    addMessageToConversation(msg)
                    setSending(false)
                    resolve()
                  })
                  .catch((e: unknown) => {
                    const err = e as { status?: number }
                    if (err?.status === 403 && !isAdmin) {
                      // User not a participant - try to get/create correct conversation
                      resetChatState()
                      chatSupportService.getOrCreateConversationWithAdmin().then((correctConv) => {
                        setConversations([correctConv])
                        setSelectedConversationState(correctConv)
                        setError('Switched to your conversation with admin. Please try sending again.')
                      }).catch(() => {
                        setError("You don't have permission to send messages.")
                      })
                    } else if (isUnavailable(e)) {
                      setError("You don't have permission to send messages.")
                    } else {
                      setError((e as Error)?.message || 'Failed to send')
                    }
                    setSending(false)
                    resolve()
                  })
              }
            }
          )
        })
      }

      try {
        const msg = await chatSupportService.sendMessage(conversationId, trimmedContent)
        addMessageToConversation(msg)
      } catch (e: unknown) {
        const err = e as { status?: number }
        if (err?.status === 403 && !isAdmin) {
          // User not a participant - try to get/create correct conversation
          resetChatState()
          try {
            const correctConv = await chatSupportService.getOrCreateConversationWithAdmin()
            setConversations([correctConv])
            setSelectedConversationState(correctConv)
            setError('Switched to your conversation with admin. Please try sending again.')
          } catch {
            setError("You don't have permission to send messages.")
          }
        } else if (isUnavailable(e) && err?.status === 403) {
          setError("You don't have permission to send messages.")
        } else {
          setError((e as Error)?.message || 'Failed to send')
        }
      } finally {
        setSending(false)
      }
    },
    [selectedConversation, aiConversation, currentUserId, isAdmin, resetChatState]
  )

  /**
   * Join existing conversation with admin (non-admin only).
   * Gets or creates conversation, then joins (prefer join-room with roomId). Handles auto-fix from backend.
   */
  const joinExistingConversationWithAdmin = useCallback(async () => {
    if (isAdmin) return
    setLoading(true)
    setError(null)
    try {
      const conv = await chatSupportService.getOrCreateConversationWithAdmin()
      setChatAvailable(true)
      setConversations([conv])
      setSelectedConversationState(conv)
      const conversationId = getConversationId(conv)
      if (!conversationId) throw new Error('Conversation missing ID')
      const roomId = getRoomId(conv)
      const socket = socketService.getSocket()

      if (socket?.connected) {
        const onJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
          if ((res as AckError).error) {
            const errRes = res as AckError
            if (process.env.NODE_ENV === 'development') console.warn('[chat] join failed', errRes.status, errRes.error)
            setError(errRes.error || 'Could not join chat room')
          } else {
            const successRes = res as JoinAckSuccess
            if (successRes.autoFixed && successRes.conversationId) {
              const fixedConvId = successRes.conversationId
              chatSupportService.fetchConversations().then((list) => {
                const fixedConv = list.find((c) => getConversationId(c) === fixedConvId)
                if (fixedConv) {
                  setConversations([fixedConv])
                  setSelectedConversationState(fixedConv)
                  if (successRes.message) {
                    setError(null)
                    if (process.env.NODE_ENV === 'development') console.info('[chat]', successRes.message)
                  }
                  loadMessages(fixedConvId)
                }
              })
            } else {
              loadMessages(conversationId)
            }
          }
        }
        if (roomId) socket.emit('join-room', { roomId }, onJoin)
        else socket.emit('conversation:join', { conversationId }, onJoin)
      } else {
        loadMessages(conversationId)
      }
    } catch (e: unknown) {
      if (isUnavailable(e)) {
        setChatAvailable(false)
        const status = (e as { status?: number })?.status
        setError(
          status === 500
            ? 'Chat service is temporarily unavailable. Please try again later.'
            : 'Chat is not available yet.'
        )
      } else {
        setError((e as Error)?.message || 'Failed to join conversation')
      }
      resetChatState()
    } finally {
      setLoading(false)
    }
  }, [isAdmin, loadMessages, resetChatState])

  /**
   * Start a new conversation with admin (non-admin only).
   * Backend archives current conversation (history kept) and creates a new active one.
   * Leave previous Socket room (if joined), full reset of local state, then join new conversation.
   */
  const startNewConversationWithAdmin = useCallback(async () => {
    if (isAdmin) return
    const prevConvId = getConversationId(selectedConversation)
    const prevRoomId = getRoomId(selectedConversation)
    const socket = socketService.getSocket()
    if (socket?.connected && (prevConvId || prevRoomId)) {
      const leavePayload: { conversationId?: string; roomId?: string } = {}
      if (prevConvId) leavePayload.conversationId = prevConvId
      if (prevRoomId) leavePayload.roomId = prevRoomId
      socket.emit('conversation:leave', leavePayload, () => {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[chat] left previous conversation before start-new', prevConvId, prevRoomId ?? '')
        }
      })
    }
    resetChatState()
    setLoading(true)
    setError(null)
    try {
      const newConv = await chatSupportService.startNewConversationWithAdmin()
      setChatAvailable(true)
      setConversations([newConv])
      setSelectedConversationState(newConv)
      const newConvId = getConversationId(newConv)
      if (!newConvId) throw new Error('Conversation missing ID')
      const newRoomId = getRoomId(newConv)
      if (socket?.connected) {
        const onNewJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
          if ((res as JoinAckSuccess).success) {
            const successRes = res as JoinAckSuccess
            if (successRes.autoFixed && successRes.conversationId) {
              const fixedConvId = successRes.conversationId
              chatSupportService.fetchConversations().then((list) => {
                const fixedConv = list.find((c) => getConversationId(c) === fixedConvId)
                if (fixedConv) {
                  setConversations([fixedConv])
                  setSelectedConversationState(fixedConv)
                  loadMessages(fixedConvId)
                }
              })
            } else {
              loadMessages(newConvId)
            }
          }
          if (process.env.NODE_ENV === 'development') console.debug('[chat] joined new conversation', newConvId)
        }
        if (newRoomId) {
          socket.emit('join-room', { roomId: newRoomId }, onNewJoin)
        } else {
          socket.emit('conversation:join', { conversationId: newConvId }, onNewJoin)
        }
      } else {
        loadMessages(newConvId)
      }
    } catch (e: unknown) {
      const err = e as {
        response?: { status?: number; data?: { message?: string; en?: string; fr?: string } }
        message?: string
        status?: number
        serverMessage?: string
      }
      const status = err?.response?.status ?? err?.status
      const serverMessage =
        err?.serverMessage ||
        (e as { serverMessage?: string })?.serverMessage ||
        err?.response?.data?.message ||
        err?.response?.data?.en ||
        err?.response?.data?.fr
      if (process.env.NODE_ENV === 'development' && (status === 500 || serverMessage)) {
        console.warn('[chat] start-new 500', { status, serverMessage, data: (e as { response?: { data?: unknown } })?.response?.data })
      }
      if (status === 500) {
        // Backend error - try to fallback to get/create existing conversation
        setError(serverMessage || 'Unable to create new conversation. Loading your existing conversation...')
        try {
            const existingConv = await chatSupportService.getOrCreateConversationWithAdmin()
          setChatAvailable(true)
          setConversations([existingConv])
          setSelectedConversationState(existingConv)
          const existingConvId = getConversationId(existingConv)
          if (existingConvId) {
            const existingRoomId = getRoomId(existingConv)
            const sock = socketService.getSocket()
            if (sock?.connected) {
              const onJoin = () => loadMessages(existingConvId)
              if (existingRoomId) sock.emit('join-room', { roomId: existingRoomId }, onJoin)
              else sock.emit('conversation:join', { conversationId: existingConvId }, onJoin)
            } else {
              loadMessages(existingConvId)
            }
          }
          setError(null)
        } catch (fallbackError) {
          setError(serverMessage || 'Server error: Unable to access chat. Please try again later or contact support.')
          resetChatState()
        }
      } else if (isUnavailable(e)) {
        setChatAvailable(false)
        setError(
          status === 500
            ? 'Chat service is temporarily unavailable. Please try again later.'
            : 'Chat is not available yet.'
        )
        resetChatState()
      } else {
        setError(err?.response?.data?.message || (e as Error)?.message || 'Failed to start new conversation')
        resetChatState()
      }
    } finally {
      setLoading(false)
    }
  }, [isAdmin, selectedConversation, resetChatState, loadMessages, isUnavailable])

  /**
   * Delete a conversation and all its messages (admin only).
   * FULL reset if it was selected.
   */
  const deleteConversation = useCallback(
    async (conversationId: string) => {
      if (!isAdmin) return
      setError(null)
      try {
        await chatSupportService.deleteConversation(conversationId)
        loadedConversationIdsRef.current.delete(conversationId)
        setConversations((prev) => prev.filter((c) => getConversationId(c) !== conversationId))
        setMessagesByConversation((prev) => {
          const next = { ...prev }
          delete next[conversationId]
          return next
        })
        const selectedId = getConversationId(selectedConversation)
        if (selectedId === conversationId) {
          resetChatState()
        }
      } catch (e: unknown) {
        const res = (e as { response?: { status?: number; data?: { message?: string } } })?.response
        const status = res?.status
        if (status === 404) {
          resetChatState()
          setError('Conversation not found')
        } else if (status === 403) {
          setError('You do not have permission to delete this conversation.')
        } else {
          setError(res?.data?.message || (e as Error)?.message || 'Failed to delete conversation')
        }
      }
    },
    [isAdmin, selectedConversation, resetChatState]
  )

  /**
   * Admin: Start or open chat with a specific user.
   * Checks existing conversations first, then tries to create if not found.
   */
  const startChatWithUser = useCallback(
    async (userId: string) => {
      if (!isAdmin) return
      setLoading(true)
      setError(null)
      try {
        // Check if conversation already exists
        const existingConv = conversations.find((conv) => {
          const participantIds = conv.participants.map((p) => p._id)
          return participantIds.includes(userId) && participantIds.includes(currentUserId ?? '')
        })

        if (existingConv) {
          setSelectedConversationState(existingConv)
          const conversationId = getConversationId(existingConv)
          if (conversationId) {
            const roomId = getRoomId(existingConv)
            const socket = socketService.getSocket()
            const onJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
              if ((res as JoinAckSuccess).success) {
                const successRes = res as JoinAckSuccess
                if (successRes.autoFixed && successRes.conversationId) {
                  const fixedConvId = successRes.conversationId
                  chatSupportService.fetchConversations().then((list) => {
                    const fixedConv = list.find((c) => getConversationId(c) === fixedConvId)
                    if (fixedConv) {
                      setConversations(list)
                      setSelectedConversationState(fixedConv)
                      loadMessages(fixedConvId)
                    }
                  })
                } else {
                  loadMessages(conversationId)
                }
              } else {
                loadMessages(conversationId)
              }
            }
            if (socket?.connected) {
              if (roomId) socket.emit('join-room', { roomId }, onJoin)
              else socket.emit('conversation:join', { conversationId }, onJoin)
            } else {
              loadMessages(conversationId)
            }
          }
          setLoading(false)
          return
        }

        // Try to refresh conversations list
        try {
          const refreshedList = await chatSupportService.fetchConversations()
          const foundConv = refreshedList.find((conv) => {
            const participantIds = conv.participants.map((p) => p._id)
            return participantIds.includes(userId) && participantIds.includes(currentUserId ?? '')
          })

          if (foundConv) {
            setConversations(refreshedList)
            setSelectedConversationState(foundConv)
            const conversationId = getConversationId(foundConv)
            if (conversationId) {
              const roomId = getRoomId(foundConv)
              const socket = socketService.getSocket()
              const onJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
                if ((res as JoinAckSuccess).success) {
                  const successRes = res as JoinAckSuccess
                  if (successRes.autoFixed && successRes.conversationId) {
                    const fixedConvId = successRes.conversationId
                    chatSupportService.fetchConversations().then((list) => {
                      const fixedConv = list.find((c) => getConversationId(c) === fixedConvId)
                      if (fixedConv) {
                        setConversations([fixedConv])
                        setSelectedConversationState(fixedConv)
                        loadMessages(fixedConvId)
                      }
                    })
                  } else {
                    loadMessages(conversationId)
                  }
                } else {
                  loadMessages(conversationId)
                }
              }
              if (socket?.connected) {
                if (roomId) socket.emit('join-room', { roomId }, onJoin)
                else socket.emit('conversation:join', { conversationId }, onJoin)
              } else {
                loadMessages(conversationId)
              }
            }
            setLoading(false)
            return
          }
        } catch (refreshError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[chat] failed to refresh conversations', refreshError)
          }
        }

        // Try to create new conversation
        let conv: ChatConversation
        try {
          conv = await chatSupportService.getOrCreateConversationWithUser(userId)
        } catch (e: unknown) {
          const status = (e as AxiosError)?.response?.status
          if (status === 404) {
            setError(
              'Backend endpoint not implemented. Please implement POST /api/chat/conversations/with-user/:userId for admin to start chats with users.'
            )
            setLoading(false)
            return
          }
          throw e
        }

        setChatAvailable(true)
        setConversations((prev) => {
          const convId = getConversationId(conv)
          if (!convId) return prev
          const exists = prev.some((c) => getConversationId(c) === convId)
          if (exists) return prev
          return [...prev, conv]
        })
        setSelectedConversationState(conv)
        const conversationId = getConversationId(conv)
        if (!conversationId) throw new Error('Conversation missing ID')
        const roomId = getRoomId(conv)
        const socket = socketService.getSocket()

        const onJoin = (res: JoinAckSuccess | JoinRoomAckSuccess | AckError) => {
          if ((res as AckError).error) {
            const errRes = res as AckError
            if (process.env.NODE_ENV === 'development') console.warn('[chat] join failed', errRes.status, errRes.error)
            setError(errRes.error || 'Could not join chat room')
          } else {
            const successRes = res as JoinAckSuccess
            if (successRes.autoFixed && successRes.conversationId) {
              const fixedConvId = successRes.conversationId
              chatSupportService.fetchConversations().then((list) => {
                const fixedConv = list.find((c) => getConversationId(c) === fixedConvId)
                if (fixedConv) {
                  setConversations((prev) => {
                    const exists = prev.some((c) => getConversationId(c) === fixedConvId)
                    if (exists) return prev
                    return [...prev, fixedConv]
                  })
                  setSelectedConversationState(fixedConv)
                  if (successRes.message && process.env.NODE_ENV === 'development') console.info('[chat]', successRes.message)
                  loadMessages(fixedConvId)
                }
              })
            } else {
              loadMessages(conversationId)
            }
          }
        }
        if (socket?.connected) {
          if (roomId) socket.emit('join-room', { roomId }, onJoin)
          else socket.emit('conversation:join', { conversationId }, onJoin)
        } else {
          loadMessages(conversationId)
        }
      } catch (e: unknown) {
        if (isUnavailable(e)) {
          setChatAvailable(false)
          const status = (e as { status?: number })?.status
          setError(
            status === 500
              ? 'Chat service is temporarily unavailable. Please try again later.'
              : 'Chat is not available yet.'
          )
        } else {
          const err = e as AxiosError
          if (err?.response?.status === 404) {
            setError(
              'Backend endpoint not implemented. Please implement POST /api/chat/conversations/with-user/:userId for admin to start chats with users.'
            )
          } else {
            setError((e as Error)?.message || 'Failed to start chat with user')
          }
        }
      } finally {
        setLoading(false)
      }
    },
    [isAdmin, loadMessages, conversations, currentUserId]
  )

  const refreshConversations = useCallback(async () => {
    try {
      const list = await chatSupportService.fetchConversations()
      const filteredList = isAdmin
        ? list
        : list.filter((conv) => {
            const participantIds = conv.participants.map((p) => p._id)
            return currentUserId && participantIds.includes(currentUserId) && conv.participants.length === 2
          })
      setChatAvailable(true)
      setConversations(filteredList)
      if (!isAdmin && filteredList.length > 0) {
        const first = filteredList[0]
        const firstId = getConversationId(first)
        const selectedId = getConversationId(selectedConversation)
        if (!selectedConversation || selectedId !== firstId) {
          setSelectedConversationState(first)
        }
      }
    } catch (e: unknown) {
      if (isUnavailable(e)) setChatAvailable(false)
      setConversations([])
    }
  }, [isAdmin, currentUserId, selectedConversation])

  const totalUnread = conversations.reduce((acc, c) => acc + (c.unreadCount ?? 0), 0)
  const conversationWithAdmin = isAdmin ? null : conversations[0] ?? null

  useEffect(() => {
    if (!isAdmin && conversationWithAdmin && !selectedConversation) {
      setSelectedConversationState(conversationWithAdmin)
    }
  }, [isAdmin, conversationWithAdmin, selectedConversation])

  const isAiTyping = currentConvIdMemo ? (aiTyping[currentConvIdMemo] ?? false) : false

  return {
    conversations,
    selectedConversation,
    setSelectedConversation,
    messages: visibleMessages,
    loading,
    sending,
    error,
    chatAvailable,
    loadConversationWithAdmin,
    joinExistingConversationWithAdmin,
    loadConversations,
    refreshConversations,
    sendMessage,
    totalUnread,
    conversationWithAdmin,
    startNewConversationWithAdmin,
    startChatWithUser: startChatWithUser,
    deleteConversation,
    isAiTyping,
    loadAiConversation,
    switchToAdminConversation,
    isAiConversation,
  }
}
